import {createAction} from '@reduxjs/toolkit'
import {hasSeatingPlan, isDraft} from '@wix/wix-events-commons-statics'
import {isTemplate} from '../../../../commons/selectors/instance'
import {UserRole} from '../constants/constants'
import {isUpgradeNeededForCheckout} from '../selectors/checkout-options'
import {isCheckoutVisibilityEnabled} from '../selectors/checkout-visibility'
import {getEvent} from '../selectors/event'
import {isEventPreview} from '../selectors/navigation'
import {isRequestPending} from '../selectors/pending-requests'
import {getSeatsReservationQuantities} from '../selectors/seating/places'
import {getReservationQuantities} from '../selectors/selected-tickets'
import {CheckoutStage, CheckoutStageOutcome, getCheckoutFailure} from '../services/checkout-visibility'
import {createAsyncAction} from '../services/redux-toolkit'
import {GetState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {openCheckoutUnavailable, openUpgradeToPremium} from './modals'
import {navigate, navigateToMobileCheckout, navigateToTicketForm} from './navigation'

export const reserveTickets =
  (eventId: string) =>
  async (dispatch: Function, getState: GetState, {wixCodeApi, flowAPI, logger}: StoreExtraArgs) => {
    const state = getState()
    const upgradeNeeded = isUpgradeNeededForCheckout(state)
    const owner = wixCodeApi.user.currentUser.role === UserRole.ADMIN
    const event = getEvent(state)
    const mobile = flowAPI.environment.isMobile
    const visibilityEnabled = isCheckoutVisibilityEnabled(state)

    if (isTemplate(state) || isEventPreview(state) || isDraft(event)) {
      return mobile ? dispatch(navigateToMobileCheckout()) : dispatch(navigateToTicketForm())
    }

    if (upgradeNeeded && (mobile || !owner)) {
      if (visibilityEnabled) {
        logger.reportCheckoutStage(CheckoutStage.Eligibility, CheckoutStageOutcome.BusinessFailure, {
          reason: 'premium_upgrade_required',
        })
        logger.finishCheckoutAttempt(CheckoutStageOutcome.BusinessFailure, {reason: 'premium_upgrade_required'})
      }
      return dispatch(openCheckoutUnavailable())
    }

    const ticketQuantities: wix.events.ticketing.TicketReservationQuantity[] = hasSeatingPlan(event)
      ? getSeatsReservationQuantities(state)
      : getReservationQuantities(state)

    if (upgradeNeeded && owner) {
      const {proceed} = await dispatch(openUpgradeToPremium()).unwrap()
      if (!proceed) {
        if (visibilityEnabled) {
          logger.reportCheckoutStage(CheckoutStage.Eligibility, CheckoutStageOutcome.Cancelled, {
            reason: 'premium_upgrade_declined',
          })
          logger.finishCheckoutAttempt(CheckoutStageOutcome.Cancelled, {reason: 'premium_upgrade_declined'})
        }
        return
      }
    }

    if (visibilityEnabled) {
      logger.reportCheckoutStage(CheckoutStage.Eligibility, CheckoutStageOutcome.Success)
    }
    return dispatch(makeReservationAndNavigate({eventId, ticketQuantities, visibilityEnabled}))
  }

interface CancelReservationParams {
  eventId: string
  reservationId: string
}

export const cancelReservation = createAsyncAction<{}, CancelReservationParams>(
  'CANCEL_RESERVATION',
  ({eventId, reservationId}, {extra: {serverApi}}) => {
    if (eventId && reservationId) {
      return serverApi.cancelReservation(eventId, reservationId)
    }
  },
)

export const makeReservation = createAsyncAction<
  {id: string; expires: string; reservations?: wix.events.ticketing.TicketReservation[]},
  {
    eventId: string
    ticketQuantities: wix.events.ticketing.TicketReservationQuantity[]
  }
>('MAKE_RESERVATION', async ({eventId, ticketQuantities}, {getState, extra: {serverApi}}) => {
  const state = getState()

  if (!state.demoTickets?.length) {
    return serverApi.makeReservation(eventId, ticketQuantities)
  } else {
    return {
      id: 'demo',
      reservations: [],
      expires: new Date(new Date().getTime() + 20 * 60 * 1000).toISOString(),
    }
  }
})

export const makeReservationAndNavigate = createAsyncAction<
  void,
  {
    eventId: string
    ticketQuantities: wix.events.ticketing.TicketReservationQuantity[]
    visibilityEnabled: boolean
  }
>(
  'MAKE_RESERVATION_AND_NAVIGATE',
  async ({eventId, ticketQuantities, visibilityEnabled}, {dispatch, extra: {flowAPI, logger}}) => {
    if (visibilityEnabled) {
      logger.reportCheckoutStage(CheckoutStage.Reservation, CheckoutStageOutcome.Started)
    }

    try {
      const reservation = await dispatch(makeReservation({eventId, ticketQuantities})).unwrap()
      const reservationId = reservation?.id

      if (visibilityEnabled) {
        logger.reportCheckoutStage(CheckoutStage.Reservation, CheckoutStageOutcome.Success)
        logger.reportCheckoutStage(CheckoutStage.CheckoutForm, CheckoutStageOutcome.Started)
      }

      const checkoutVisibility = logger.getCheckoutRouteContext()

      flowAPI.environment.isMobile
        ? dispatch(navigateToMobileCheckout(reservationId, checkoutVisibility))
        : dispatch(navigateToTicketForm(reservationId, checkoutVisibility))
    } catch (error) {
      if (visibilityEnabled) {
        const failure = getCheckoutFailure(error)
        logger.reportCheckoutStage(CheckoutStage.Reservation, failure.outcome, failure.context)
        logger.finishCheckoutAttempt(failure.outcome, failure.context)
      }
      throw error
    }
  },
  {
    condition: (_, {getState}) => {
      const state = getState()

      return !isRequestPending(state, navigate.typePrefix) && !isRequestPending(state, makeReservation.typePrefix)
    },
  },
)

export const clearReservation = createAction('CLEAR_RESERVATION')

export const clearReservationError = createAction<boolean>('CLEAR_RESERVATION_ERROR')
